'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Star, User } from 'lucide-react'

export type Testimonial = {
  name: string
  loc: string
  text: string
  stars: number
}

/**
 * Movable testimonial carousel.
 *
 * - Shows MULTIPLE cards per viewport (1 mobile, 2 tablet, 3 desktop)
 *   so the section never looks empty on wide screens. Card widths are
 *   controlled by `.testimonial-card` rules in globals.css.
 * - Advances ONE card at a time so each transition is gentle, looping
 *   back to the start when the last card is fully in view.
 * - Dots represent scroll PAGES (= items - perView + 1), not items,
 *   so the indicator matches what users actually navigate through.
 * - Auto-advances every 7s, pauses on hover/focus/touch.
 * - Native scroll-snap → phone swipe gestures work for free.
 *
 * All cards are real DOM so screen readers and SEO crawlers see every
 * review; carousel chrome is keyboard- and aria-labelled.
 */
export default function TestimonialCarousel({
  items,
  autoAdvanceMs = 7000,
}: {
  items: Testimonial[]
  autoAdvanceMs?: number
}) {
  const trackRef = useRef<HTMLDivElement | null>(null)
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  // Cards visible per viewport — recomputed on resize. 1 on mobile,
  // 2 on tablet, 3 on desktop (matches the breakpoints in globals.css).
  // Drives how many dots we render and where auto-advance can stop.
  const [perView, setPerView] = useState(1)

  // How many distinct "pages" of testimonials there are. With 8 items
  // and 3 per view, there are 8 - 3 + 1 = 6 stop positions. Capped at
  // items.length so we always have at least one dot.
  const pages = Math.max(1, items.length - perView + 1)

  const scrollTo = useCallback((i: number) => {
    const track = trackRef.current
    if (!track) return
    const cards = track.children
    const max = Math.max(0, cards.length - perView)
    // Wrap around: passing -1 jumps to the last page, passing pages
    // jumps back to 0. Matches the visual loop users expect.
    const wrapped = ((i % (max + 1)) + (max + 1)) % (max + 1)
    const target = cards[wrapped] as HTMLElement | undefined
    if (target) {
      track.scrollTo({ left: target.offsetLeft - track.offsetLeft, behavior: 'smooth' })
      setIndex(wrapped)
    }
  }, [perView])

  const next = useCallback(() => scrollTo(index + 1), [index, scrollTo])
  const prev = useCallback(() => scrollTo(index - 1), [index, scrollTo])

  // Detect how many cards fit per viewport by measuring the first card.
  // Runs on mount + resize so breakpoint changes are picked up live.
  useEffect(() => {
    const track = trackRef.current
    if (!track) return
    const measure = () => {
      const first = track.children[0] as HTMLElement | undefined
      if (!first) return
      // Round to nearest int; clamp so floating-point fuzz can't give 0.
      const n = Math.max(1, Math.round(track.clientWidth / first.clientWidth))
      setPerView(Math.min(n, items.length))
    }
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [items.length])

  // Auto-advance loop. Cleared while user is interacting.
  useEffect(() => {
    if (paused) return
    const id = window.setInterval(() => {
      setIndex(i => {
        const max = Math.max(0, items.length - perView)
        const nextI = i >= max ? 0 : i + 1
        requestAnimationFrame(() => scrollTo(nextI))
        return nextI
      })
    }, autoAdvanceMs)
    return () => window.clearInterval(id)
  }, [paused, autoAdvanceMs, items.length, perView, scrollTo])

  // Keep `index` in sync when user swipes/scrolls manually so the
  // active dot reflects what they actually see.
  const onTrackScroll = useCallback(() => {
    const track = trackRef.current
    if (!track) return
    // The card whose left edge is closest to the track's scrollLeft is
    // the "page". We snap to a card's offsetLeft, so this stays stable.
    let nearest = 0
    let best = Infinity
    Array.from(track.children).forEach((c, i) => {
      const el = c as HTMLElement
      const d = Math.abs((el.offsetLeft - track.offsetLeft) - track.scrollLeft)
      if (d < best) { best = d; nearest = i }
    })
    setIndex(nearest)
  }, [])

  return (
    <div
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onTouchStart={() => setPaused(true)}
      onTouchEnd={() => setPaused(false)}
      aria-roledescription="carousel"
      aria-label="User testimonials"
    >
      {/* Track */}
      <div
        ref={trackRef}
        onScroll={onTrackScroll}
        className="testimonial-track"
        style={{
          display: 'flex',
          gap: '20px',
          overflowX: 'auto',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'none',
          paddingBottom: '8px',
        }}
      >
        {items.map((t, i) => (
          <div
            key={i}
            className="testimonial-card"
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${items.length}`}
            style={{ scrollSnapAlign: 'start' }}
          >
            <div style={{ display: 'flex', gap: '4px', marginBottom: '12px' }}>
              {Array.from({ length: t.stars }).map((_, j) => (
                <Star key={j} size={16} color="#f0a050" fill="#f0a050" />
              ))}
            </div>
            <p style={{ color: 'var(--text-secondary)', fontSize: '15px', lineHeight: 1.7, marginBottom: '18px', fontStyle: 'italic' }}>
              &ldquo;{t.text}&rdquo;
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 42, height: 42, borderRadius: '50%', background: 'var(--bg-secondary)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <User size={20} color="var(--accent)" />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)' }}>{t.name}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{t.loc}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', marginTop: '24px' }}>
        <button
          type="button"
          onClick={prev}
          aria-label="Previous testimonial"
          className="carousel-btn"
        >
          <ChevronLeft size={20} />
        </button>

        <div role="tablist" aria-label="Choose testimonials page" style={{ display: 'flex', gap: '8px' }}>
          {Array.from({ length: pages }).map((_, i) => {
            const active = i === index
            return (
              <button
                key={i}
                type="button"
                role="tab"
                aria-selected={active}
                aria-label={`Go to testimonials page ${i + 1}`}
                onClick={() => scrollTo(i)}
                style={{
                  width: active ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '100px',
                  border: 'none',
                  background: active ? 'var(--accent)' : 'var(--border)',
                  cursor: 'pointer',
                  transition: 'all 0.25s',
                  padding: 0,
                }}
              />
            )
          })}
        </div>

        <button
          type="button"
          onClick={next}
          aria-label="Next testimonial"
          className="carousel-btn"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Carousel-specific CSS (track + button + responsive widths) lives
          in app/globals.css. Inlining a <style> here would put a `>`
          combinator into the text node, which React serialises as `&gt;`
          on the server but parses as `>` on the client — that mismatch
          triggers a hydration error. */}
    </div>
  )
}
